/**
 * ActionCalendar - Main container for the Action Calendar view.
 *
 * Provides mode switching (Day/3-Day/Schedule), filters, and toggles.
 * Inspired by Google Calendar mobile Day/3-Day/Schedule views.
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { PlanStep, StepStatus, Participant, PlanRole } from '../../../types/generated/graphql';
import { scheduleSteps, ScheduleResult } from '../../../utils/calendarScheduler';
import { colors } from '../../../theme/colors';
import { useAuth } from '../../../contexts/AuthContext';
import { resolveCurrentParticipantId } from '../../../utils/planHelpers';
import DayView from './DayView';
import ThreeDayView from './ThreeDayView';
import ScheduleView from './ScheduleView';
import AnchorBanner from './AnchorBanner';

export type CalendarMode = 'day' | '3-day' | 'upcoming';
export type FilterType = 'all' | 'mine' | 'waiting';

interface ActionCalendarProps {
  steps: PlanStep[];
  participants: Participant[];
  roles?: PlanRole[];
  facts?: Record<string, unknown> | null;
  onNavigateToTask: (taskId: string) => void;
}

/**
 * Get the start of today (midnight).
 */
function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Filter schedule result based on toggles.
 * Note: scheduleSteps already separates my tasks from waiting tasks.
 * This filter only handles completed/inactive status toggles.
 */
function filterScheduleResult(
  result: ScheduleResult,
  showCompleted: boolean,
  showInactive: boolean,
  filter: FilterType
): ScheduleResult {
  // Filter the step schedule map based on status
  const filteredStepSchedule = new Map(result.stepSchedule);
  for (const [stepId, scheduledStep] of Array.from(result.stepSchedule.entries())) {
    const step = scheduledStep.step;

    // Filter completed
    if (!showCompleted && step.status === StepStatus.Completed) {
      filteredStepSchedule.delete(stepId);
      continue;
    }

    // Filter inactive
    if (!showInactive && step.status === StepStatus.Skipped) {
      filteredStepSchedule.delete(stepId);
      continue;
    }
  }

  // Rebuild days with filtered tasks
  const filteredDays = result.days.map((day) => {
    // Filter my tasks (in segments)
    const morningTasks = day.morningTasks.filter((t) => filteredStepSchedule.has(t.step.id));
    const afternoonTasks = day.afternoonTasks.filter((t) => filteredStepSchedule.has(t.step.id));
    const eveningTasks = day.eveningTasks.filter((t) => filteredStepSchedule.has(t.step.id));
    const allDayTasks = day.allDayTasks.filter((t) => filteredStepSchedule.has(t.step.id));

    // Filter waiting tasks based on filter mode
    let waitingTasks = day.waitingTasks.filter((t) => filteredStepSchedule.has(t.step.id));

    // Apply filter mode:
    // - 'all': show both my tasks and waiting tasks
    // - 'mine': hide waiting tasks
    // - 'waiting': hide my tasks, only show waiting
    if (filter === 'mine') {
      waitingTasks = []; // Hide waiting tasks when "Mine" is selected
    }

    return {
      ...day,
      morningTasks: filter === 'waiting' ? [] : morningTasks,
      afternoonTasks: filter === 'waiting' ? [] : afternoonTasks,
      eveningTasks: filter === 'waiting' ? [] : eveningTasks,
      allDayTasks: filter === 'waiting' ? [] : allDayTasks,
      waitingTasks,
    };
  });

  // Remove empty days
  const nonEmptyDays = filteredDays.filter(
    (day) =>
      day.morningTasks.length > 0 ||
      day.afternoonTasks.length > 0 ||
      day.eveningTasks.length > 0 ||
      day.allDayTasks.length > 0 ||
      day.waitingTasks.length > 0
  );

  return {
    stepSchedule: filteredStepSchedule,
    days: nonEmptyDays,
    anchors: result.anchors,
  };
}

export const ActionCalendar: React.FC<ActionCalendarProps> = ({
  steps,
  participants,
  roles = [],
  facts,
  onNavigateToTask,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.uid || null;

  // Find current user's participant ID (with email fallback for unlinked participants)
  const currentParticipantId = useMemo(() => {
    return resolveCurrentParticipantId(currentUserId, user?.email, participants);
  }, [currentUserId, user?.email, participants]);

  // State
  const [mode, setMode] = useState<CalendarMode>('day');
  const [currentDate, setCurrentDate] = useState<Date>(getToday());
  const [filter, setFilter] = useState<FilterType>('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Schedule all steps (scheduler separates my tasks from waiting tasks)
  const rawScheduleResult = useMemo(
    () => scheduleSteps(steps, currentParticipantId, facts),
    [steps, currentParticipantId, facts]
  );

  // Apply filters (status toggles and filter mode)
  const scheduleResult = useMemo(
    () =>
      filterScheduleResult(
        rawScheduleResult,
        showCompleted,
        showInactive,
        filter
      ),
    [rawScheduleResult, showCompleted, showInactive, filter]
  );

  // Handlers
  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: CalendarMode | null
  ) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: FilterType | null
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  return (
    <Box>
      {/* Controls bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        {/* Mode selector */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontSize: '0.8rem',
              px: 2,
              py: 0.5,
            },
          }}
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="3-day">3-Day</ToggleButton>
          <ToggleButton value="upcoming">Upcoming</ToggleButton>
        </ToggleButtonGroup>

        {/* Right side controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Filter toggle */}
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={handleFilterChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.25,
              },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="mine">Mine</ToggleButton>
            <ToggleButton value="waiting">Waiting</ToggleButton>
          </ToggleButtonGroup>

          {/* Show toggles */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Completed</Typography>}
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="caption">Inactive</Typography>}
              sx={{ mr: 0 }}
            />
          </Box>
        </Box>
      </Box>

      {/* Anchor banner */}
      <AnchorBanner anchors={rawScheduleResult.anchors} />

      {/* View content */}
      <Box>
        {mode === 'day' && (
          <DayView
            scheduleResult={scheduleResult}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            participants={participants}
            roles={roles}
            currentParticipantId={currentParticipantId}
            onNavigateToTask={onNavigateToTask}
          />
        )}

        {mode === '3-day' && (
          <ThreeDayView
            scheduleResult={scheduleResult}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            participants={participants}
            roles={roles}
            currentParticipantId={currentParticipantId}
            onNavigateToTask={onNavigateToTask}
          />
        )}

        {mode === 'upcoming' && (
          <ScheduleView
            scheduleResult={scheduleResult}
            participants={participants}
            roles={roles}
            currentParticipantId={currentParticipantId}
            onNavigateToTask={onNavigateToTask}
          />
        )}
      </Box>

      {/* Empty state for filtered results */}
      {scheduleResult.stepSchedule.size === 0 && rawScheduleResult.stepSchedule.size > 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: colors.gray[500],
          }}
        >
          <Typography variant="body2">
            {filter === 'mine'
              ? 'No tasks assigned to you'
              : filter === 'waiting'
              ? 'No tasks assigned to others'
              : 'No tasks match current filters'}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray[400] }}>
            Try adjusting the filters above
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ActionCalendar;
